import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { osirisTokens } from '@/theme/tokens';

export type StatusDotTone = 'neutral' | 'success' | 'error' | 'warning' | 'info' | 'live';

interface StatusDotProps {
  tone?: StatusDotTone;
  children: ReactNode;
  className?: string;
}

const TONE_COLORS: Record<StatusDotTone, { dot: string; text: string }> = {
  neutral: { dot: osirisTokens.icons.tertiary, text: osirisTokens.text.tertiary },
  success: { dot: osirisTokens.text.success, text: osirisTokens.text.success },
  error: { dot: osirisTokens.text.error, text: osirisTokens.text.error },
  warning: { dot: osirisTokens.text.warning, text: osirisTokens.text.warning },
  info: { dot: osirisTokens.status.info, text: osirisTokens.status.info },
  live: { dot: osirisTokens.text.error, text: osirisTokens.text.error },
};

/**
 * Figma status badge: 8px colored dot + mono 12 caps label in the same tone
 * (UNVERIFIED / ONLINE / FAILED / QUEUED / RUNNING / LIVE).
 */
export function StatusDot({ tone = 'neutral', children, className }: StatusDotProps) {
  const colors = TONE_COLORS[tone];
  return (
    <Box
      component="span"
      className={`status-dot status-dot-${tone}${className ? ' ' + className : ''}`}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: colors.dot,
          flexShrink: 0,
        }}
      />
      <Box
        component="span"
        sx={{
          fontFamily: osirisTokens.fonts.mono,
          fontSize: 12,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: colors.text,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
